import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  buildContentTrackingMetadata,
  getApiBaseUrl,
  getStoredContentSessionForRoute,
} from "../lib/gatedContentTracking";

const TRACKED_CONTENT_TYPES = new Set(["document", "quote", "offer"]);

const GatedContentViewTracker = () => {
  const location = useLocation();
  const [sessionVersion, setSessionVersion] = useState(0);
  const session = getStoredContentSessionForRoute(location.pathname);
  const metadata = useMemo(
    () =>
      session
        ? buildContentTrackingMetadata({
            contentId: session.contentId,
            contentType: session.contentType,
            contentTitle: session.contentTitle,
          })
        : null,
    [session, location.pathname]
  );

  useEffect(() => {
    const handleSessionStored = () => setSessionVersion((value) => value + 1);
    window.addEventListener("asha:gated-content-session", handleSessionStored);
    return () =>
      window.removeEventListener(
        "asha:gated-content-session",
        handleSessionStored
      );
  }, []);

  useEffect(() => {
    if (!session?.sessionId || !metadata) return;
    if (!TRACKED_CONTENT_TYPES.has(metadata.contentType)) return;

    let lastFlushAt = Date.now();
    const url = `${getApiBaseUrl()}/api/slideshow/content-view`;

    const send = (durationMs, incrementViewCount, useBeacon = false) => {
      const durationSeconds = Math.max(0, Math.round(durationMs / 1000));
      if (!incrementViewCount && durationSeconds <= 0) return;

      const payload = JSON.stringify({
        sessionId: session.sessionId,
        durationSeconds,
        incrementViewCount,
        ...metadata,
      });

      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(
          url,
          new Blob([payload], { type: "application/json" })
        );
        return;
      }

      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      }).catch((err) => console.error("Failed to record content view", err));
    };

    send(0, true);

    const flush = (useBeacon = false) => {
      const now = Date.now();
      const durationMs = now - lastFlushAt;
      lastFlushAt = now;
      send(durationMs, false, useBeacon);
    };

    const flushOnHide = () => flush(true);
    window.addEventListener("pagehide", flushOnHide);

    return () => {
      window.removeEventListener("pagehide", flushOnHide);
      flush();
    };
  }, [
    session?.sessionId,
    sessionVersion,
    metadata?.contentId,
    metadata?.contentRoute,
    metadata?.contentTitle,
    metadata?.contentType,
    metadata?.contentUrl,
  ]);

  return null;
};

export default GatedContentViewTracker;
